/**
 * A small store kept on the phone: a stable snapshot for React's useSyncExternalStore, storage
 * wrapped so a full or blocked disk never breaks the app (it keeps working in memory).
 */
export interface PersistedStore<T> {
  get(): T;
  update(change: (s: T) => T): void;
  subscribe(listener: () => void): () => void;
}

export function createPersistedStore<T extends object>(storageKey: string, initial: () => T): PersistedStore<T> {
  let state: T;
  try {
    const raw = localStorage.getItem(storageKey);
    state = raw ? { ...initial(), ...(JSON.parse(raw) as T) } : initial();
  } catch {
    state = initial();
  }
  const listeners = new Set<() => void>();
  return {
    get: () => state,
    update(change) {
      const next = change(state);
      if (next === state) return;
      state = next;
      try {
        localStorage.setItem(storageKey, JSON.stringify(state));
      } catch {
        // Kept in memory for this session.
      }
      listeners.forEach((l) => l());
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}
